/**
 * AssetLedger Pro — App Router
 */

import { lazy, Suspense, useEffect } from 'react';
import { createBrowserRouter, RouterProvider, Navigate, Outlet } from 'react-router-dom';
import { useAuthStore } from '@/store/auth.store';
import { api } from '@/lib/api';
import AppLayout from '@/components/layout/AppLayout';

// Auth
const LoginPage          = lazy(() => import('@/pages/auth/LoginPage'));
const ForgotPasswordPage = lazy(() => import('@/pages/auth/ForgotPasswordPage'));
const ResetPasswordPage  = lazy(() => import('@/pages/auth/ResetPasswordPage'));
const VerifyEmailPage    = lazy(() => import('@/pages/auth/VerifyEmailPage'));

// Main pages
const DashboardPage      = lazy(() => import('@/pages/dashboard/DashboardPage'));
const AssetListPage      = lazy(() => import('@/pages/assets/AssetListPage'));
const AssetDetailPage    = lazy(() => import('@/pages/assets/AssetDetailPage'));
const AssetFormPage      = lazy(() => import('@/pages/assets/AssetFormPage'));
const DeletedAssetsPage  = lazy(() => import('@/pages/assets/DeletedAssetsPage'));
const MigrationWizardPage = lazy(() => import('@/pages/migration/MigrationWizardPage'));
const BlocksPage         = lazy(() => import('@/pages/blocks/BlocksPage'));
const CategoriesPage     = lazy(() => import('@/pages/categories/CategoriesPage'));
const CWIPPage           = lazy(() => import('@/pages/cwip/CWIPPage'));
const ApprovalsPage      = lazy(() => import('@/pages/approvals/ApprovalsPage'));
const DepreciationPage   = lazy(() => import('@/pages/depreciation/DepreciationPage'));
const DisposalsPage      = lazy(() => import('@/pages/disposals/DisposalsPage'));
const SalesImportPage    = lazy(() => import('@/pages/disposals/SalesImportPage'));
const VerificationPage   = lazy(() => import('@/pages/verification/VerificationPage'));
const QRPrintPage        = lazy(() => import('@/pages/verification/QRPrintPage'));
const InsurancePage      = lazy(() => import('@/pages/insurance/InsurancePage'));
const ImportPage         = lazy(() => import('@/pages/assets/ImportPage'));
const SplitQuantitiesPage = lazy(() => import('@/pages/assets/SplitQuantitiesPage'));
const SubsidyCorrectionPage = lazy(() => import('@/pages/assets/SubsidyCorrectionPage'));
const AdditionalDepreciationPage = lazy(() => import('@/pages/assets/AdditionalDepreciationPage'));
const BranchesPage       = lazy(() => import('@/pages/branches/BranchesPage'));
const ReportsPage        = lazy(() => import('@/pages/reports/ReportsPage'));
const UsersPage          = lazy(() => import('@/pages/users/UsersPage'));
const SettingsPage       = lazy(() => import('@/pages/settings/SettingsPage'));
const QRScanPage         = lazy(() => import('@/pages/QRScanPage'));

// Admin
const AdminDashboard       = lazy(() => import('@/pages/admin/AdminDashboard'));
const AdminClientsPage     = lazy(() => import('@/pages/admin/AdminClientsPage'));
const AdminUsersPage       = lazy(() => import('@/pages/admin/AdminUsersPage'));
const AdminAuditLogsPage   = lazy(() => import('@/pages/admin/AdminAuditLogsPage'));
const AdminSettingsPage    = lazy(() => import('@/pages/admin/AdminSettingsPage'));
const AdminPurgePage       = lazy(() => import('@/pages/admin/AdminPurgePage'));

// Background Jobs
const BackgroundJobsPage   = lazy(() => import('@/pages/jobs/BackgroundJobsPage'));

// 404
const NotFoundPage       = lazy(() => import('@/pages/NotFoundPage'));
const ProfilePage        = lazy(() => import('@/pages/profile/ProfilePage'));

function PageLoader() {
  return (
    <div className="flex items-center justify-center h-64">
      <div className="w-8 h-8 border-2 border-brand-600 border-t-transparent rounded-full animate-spin" />
    </div>
  );
}

function RequireAuth({ allowedRoles }: { allowedRoles?: string[] }) {
  const { isAuthenticated, user, setUser } = useAuthStore();

  // Hydrate the cached user object on every mount so fresh backend fields
  // (e.g. client summary added later, role promotions, 2FA toggles) become
  // visible without forcing a logout. Zustand persists the user in
  // sessionStorage, which means without this call a user can end up with a
  // stale object for the lifetime of their tab.
  useEffect(() => {
    if (!isAuthenticated) return;
    let cancelled = false;
    api.get('/auth/me')
      .then(resp => {
        const fresh = resp.data?.data;
        if (!cancelled && fresh && fresh.id) setUser(fresh);
      })
      .catch(() => { /* 401 already handled by the axios interceptor */ });
    return () => { cancelled = true; };
  }, [isAuthenticated, setUser]);

  if (!isAuthenticated) return <Navigate to="/login" replace />;
  if (allowedRoles && user && !allowedRoles.includes(user.role)) return <Navigate to="/dashboard" replace />;
  return <Suspense fallback={<PageLoader />}><Outlet /></Suspense>;
}

function RedirectIfAuth() {
  const { isAuthenticated, user } = useAuthStore();
  if (isAuthenticated) return <Navigate to={user?.role === 'SUPER_ADMIN' ? '/admin' : '/dashboard'} replace />;
  return <Suspense fallback={<PageLoader />}><Outlet /></Suspense>;
}

const router = createBrowserRouter([
  // Public auth
  { element: <RedirectIfAuth />, children: [
    { path: '/login', element: <LoginPage /> },
    { path: '/forgot-password', element: <ForgotPasswordPage /> },
    { path: '/reset-password', element: <ResetPasswordPage /> },
    { path: '/verify-email', element: <VerifyEmailPage /> },
  ]},

  // QR scan (public)
  { path: '/scan/:assetUuid', element: <Suspense fallback={<PageLoader />}><QRScanPage /></Suspense> },
  { path: '/v/:assetUuid',    element: <Suspense fallback={<PageLoader />}><QRScanPage /></Suspense> },

  // Client routes
  { element: <RequireAuth allowedRoles={['CLIENT_ADMIN', 'ACCOUNTANT', 'VERIFIER', 'AUDITOR']} />,
    children: [{ element: <AppLayout />, children: [
      { index: true, element: <Navigate to="/dashboard" replace /> },
      { path: '/dashboard', element: <DashboardPage /> },
      { path: '/assets', element: <AssetListPage /> },
      { path: '/assets/deleted', element: <DeletedAssetsPage /> },
      { path: '/assets/new', element: <AssetFormPage /> },
      { path: '/assets/:id', element: <AssetDetailPage /> },
      { path: '/assets/:id/edit', element: <AssetFormPage /> },
      { path: '/blocks', element: <BlocksPage /> },
      { path: '/categories', element: <CategoriesPage /> },
      { path: '/cwip', element: <CWIPPage /> },
      { path: '/depreciation/run', element: <DepreciationPage /> },
      { path: '/depreciation/history', element: <DepreciationPage /> },
      { path: '/disposals', element: <DisposalsPage /> },
      { path: '/disposals/import', element: <SalesImportPage /> },
      { path: '/approvals', element: <ApprovalsPage /> },
      { path: '/verification', element: <VerificationPage /> },
      { path: '/verification/print', element: <QRPrintPage /> },
      { path: '/insurance', element: <InsurancePage /> },
      { path: '/reports/:reportType?', element: <ReportsPage /> },
      { path: '/users', element: <UsersPage /> },
      { path: '/settings', element: <SettingsPage /> },
      { path: '/migration', element: <MigrationWizardPage /> },
      { path: '/profile', element: <ProfilePage /> },
      { path: '/assets/import', element: <ImportPage /> },
      { path: '/assets/split-quantities', element: <SplitQuantitiesPage /> },
      { path: '/assets/record-subsidy', element: <SubsidyCorrectionPage /> },
      { path: '/assets/additional-depreciation', element: <AdditionalDepreciationPage /> },
      { path: '/branches', element: <BranchesPage /> },
      { path: '/jobs', element: <BackgroundJobsPage /> },
    ]}]},

  // Super admin
  { element: <RequireAuth allowedRoles={['SUPER_ADMIN']} />,
    children: [{ element: <AppLayout />, children: [
      { path: '/admin', element: <AdminDashboard /> },
      { path: '/admin/clients', element: <AdminClientsPage /> },
      { path: '/admin/users', element: <AdminUsersPage /> },
      { path: '/admin/audit-logs', element: <AdminAuditLogsPage /> },
      { path: '/admin/settings', element: <AdminSettingsPage /> },
      { path: '/admin/purge', element: <AdminPurgePage /> },
    ]}]},

  // 404
  { path: '*', element: <Suspense fallback={<PageLoader />}><NotFoundPage /></Suspense> },
]);

export default function App() {
  return <RouterProvider router={router} />;
}
